<template>
  <div class="c-config-input-type c-config-input-type--column">
    <div class="c-config-input-info">
      <span>{{ input.label }}</span>
      <p>{{ input.description }}</p>
    </div>

    <md-tabs
      class="c-tabs c-tabs--small"
      :md-active-tab="selectedLanguage"
    >
      <template slot="md-tab" slot-scope="{ tab }">
        <md-icon v-if="!!tab.data.isFallback" :style="{color: !!tab.data.isSelected ? 'white' : 'black' }">
          check
        </md-icon>
        {{ tab.label }}
      </template>
      <md-tab
        v-for="language in languages"
        :id="language"
        :key="`${input.id}-${language}`"
        :md-label="language"
        :md-template-data="{ isFallback: fallbackLanguage === language, isSelected: selectedLanguage === language }"
        @click="changeLanguage(language)"
      />
    </md-tabs>

    <div>
      <generic-button v-bind:disabled="selectedLanguage === fallbackLanguage"  @click="setFallbackLanguage(selectedLanguage)">
        Set as fallback language
      </generic-button>
    </div>

    <div class="c-config-input-type c-config-input-type--column">
      <md-field>
        <label for="qmessage">Questionnaire message</label>
        <md-textarea
          v-model="getSelectedQuestionnaire().settings.questionnaireMessage"
          id="qmessage"
          name="qmessage"
          autocomplete="off"
          @input="setValue"
          md-autogrow
        />
      </md-field>
      <div v-if="getSelectedQuestionnaire().settings.questionnaireMessage.trim() === ''" class="c-hint">
        Will use fallback if empty
      </div>
    </div>
    <div class="c-config-input-type c-config-input-type--column">
      <md-field>
        <label for="dmessage">Deactivation message</label>
        <md-textarea
          v-model="getSelectedQuestionnaire().settings.deactivationMessage"
          id="dmessage"
          name="dmessage"
          autocomplete="off"
          @input="setValue"
          md-autogrow
        />
      </md-field>
      <div v-if="getSelectedQuestionnaire().settings.deactivationMessage.trim() === ''" class="c-hint">
        Will use fallback if empty
      </div>
    </div>
    <div class="c-table-wrapper c-table--online-websites c-config-column-content">
      <base-table-component :prop-data="tableData" is-ticket-banner>
        <template v-slot:action>
          <div class="header-table-section">
            Questions
          </div>
          <div class="add-button">
            <div>
              List of questions
            </div>
            <div v-if="!disabled" class="add-section">
              <img src="../../assets/add-button.svg" class="add-button__new" alt="" @click="createOrUpdateQuestion()"><span>Add new question</span>
            </div>
          </div>
        </template>
        <template v-slot:default="slotProps">
          <div class="c-buttons-wrapper">
            <span class="c-table__btn-wrap u-typography-color c-buttons">
              <generic-button
                icon
                transparent
                text-inherit
                @click="moveUp(slotProps.selected.question, $event.shiftKey)"
              >
                <md-tooltip md-direction="top">Move up (Shift-click move to top)</md-tooltip>
                <md-icon>arrow_upward</md-icon>
              </generic-button>
              <generic-button
                icon
                transparent
                text-inherit
                @click="moveDown(slotProps.selected.question, $event.shiftKey)"
              >
                <md-tooltip md-direction="top">Move down (Shift-click move to bottom)</md-tooltip>
                <md-icon>arrow_downward</md-icon>
              </generic-button>
            </span>
            <span class="c-table__btn-wrap c-buttons">
              <generic-button
                icon
                transparent
                text-inherit
                :disabled="disabled"
                @click="createOrUpdateQuestion(slotProps.selected.question)"
              >
                <md-tooltip md-direction="top"> Edit </md-tooltip>
                <img src="../../assets/edit-button.svg" alt="">
              </generic-button>
              <generic-button
                icon
                transparent
                text-inherit
                :disabled="disabled"
                @click="openDeleteModal(slotProps.selected.question)"
              >
                <md-tooltip md-direction="top"> Delete </md-tooltip>
                <img src="../../assets/delete-button.svg" alt="">
              </generic-button>
            </span>
          </div>
        </template>
      </base-table-component>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { get, cloneDeep } from 'lodash'
import GenericButton from '../buttons/GenericButton'
import BaseTableComponent from '@/shared/table/BaseTableComponent'
import CreateQuestionModal from './modals/question'
import DeleteModalComponent from '@/shared/delete-modal/DeleteModalComponent'
import ConfirmationModalComponent from '@/shared/confirmation-modal/ConfirmationModalComponent';

export default {
  name: 'QuestionnaireFormField',

  components: {
    GenericButton,
    BaseTableComponent,
  },

  props: {
    input: {
      type: Object,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false,
      required: false
    },
    languages: {
      type: Array,
      required: true
    },
  },

  data() {
    return {
      questionnairesByLanguages: this.emptyQuestionnairesByLanguages(),
      selectedLanguage: this.languages[0],
    }
  },

  computed: {
    defaultQuestionnairesByLanguages() {
      try {
        return JSON.parse(get(this.input, 'defaultValue', this.emptyQuestionnairesByLanguages()))
      } catch (e) {
        return this.emptyQuestionnairesByLanguages()
      }
    },

    tableData() {
      const questions = [];

      this.getSelectedQuestionnaire().questionnaire.forEach(question => {
        let type = '';
        if (question.type === 'RADIO') {
          type = 'Radio'
        } else if (question.type === 'YESNO') {
          type = 'Yes/No'
        } else if (question.type === 'TEXT') {
          type = 'Text';
        }
        const dep = this.getDependency(question)
        questions.push({
          question: question,
          rowData: {
            'ID': question.ID,
            'Type': type,
            'Question': question.question,
            'Dependency': dep !== undefined ? dep.ID : ''
          }
        });
      });

      return questions;
    },

    fallbackLanguage() {
      let language = '';
      Object.keys(this.questionnairesByLanguages).forEach(l => {
        if (this.questionnairesByLanguages[l].isFallback) {
          language = l;
        }
      });
      if (!language) {
        language = Object.keys(this.questionnairesByLanguages)[0];
      }
      return language;
    },
  },

  created() {
    this.questionnairesByLanguages = this.transformAllFromJson(
      get(this.input, 'value', this.defaultQuestionnairesByLanguages) || this.emptyQuestionnairesByLanguages()
    );
    this.addMissingLanguagesIfAny();
    this.setFallbackLanguageIfNeeded();
  },

  methods: {
    emptyQuestionnairesByLanguages() {
      const wrappedInLang = {};
      for (let lang of this.languages) {
        wrappedInLang[lang] = this.emptyQuestionnaire();
      }
      return wrappedInLang;
    },

    emptyQuestionnaire() {
      return { settings: { deactivationMessage: '', questionnaireMessage: '' }, questionnaire: [] };
    },

    addMissingLanguagesIfAny() {
      const questionnaireLanguages = Object.keys(this.questionnairesByLanguages);
      const missingLanguages = this.languages.filter(language => !questionnaireLanguages.includes(language));
      missingLanguages.forEach(language => {
        Vue.set(this.questionnairesByLanguages, language, this.emptyQuestionnaire());
      });
      this.questionnairesByLanguages[this.fallbackLanguage].questionnaire.forEach(q => {
        missingLanguages.forEach(language => {
          const newQ = cloneDeep(q);
          newQ.question = '';
          if (newQ.answers) {
            newQ.answers.forEach(a => a.message = '');
          }
          this.questionnairesByLanguages[language].questionnaire.push(newQ);
        });
      });
    },

    setFallbackLanguageIfNeeded() {
      Vue.set(this.questionnairesByLanguages[this.fallbackLanguage], 'isFallback', true);
    },

    setFallbackLanguage(language) {
      this.$modal.show(
        ConfirmationModalComponent,
        {
          confirmationMsg: `Are you sure you want to change the fallback language?`,
          onAccept: () => {
            Vue.delete(this.questionnairesByLanguages[this.fallbackLanguage], 'isFallback');
            Vue.set(this.questionnairesByLanguages[language], 'isFallback', true);
            this.setValue();
          },
          onClose: () => {}
        },
        { height: "auto", scrollable: false, width: "400px" }
      );
    },

    getSelectedQuestionnaire() {
      return get(this.questionnairesByLanguages, `${this.selectedLanguage}`);
    },

    createOrUpdateQuestion(question) {
      if (!question) {
        question = {
          ID: this.getSelectedQuestionnaire().questionnaire.reduce((max, cur) => cur.ID > max.ID ? cur : max, { ID: -1 }).ID + 1,
          type: "RADIO",
          question: "",
          answers: []
        }
      }

      const dep = this.getDependency(question);
      this.$modal.show(
        CreateQuestionModal,
        {
          propQuestion: question,
          questionsEligibleForDependency: this.getQuestionsEligibleForDependency(question),
          successFunction: this.addOrUpdateQuestionAll,
          dep: dep !== undefined ? dep.ID : -1,
        },
        { height: "auto", scrollable: true }
      );
    },

    openDeleteModal(selected) {
      this.$modal.show(
        DeleteModalComponent,
        {
          deleteMsg: 'Question ' + selected.ID,
          propData: selected.ID,
          deleteFunction: this.deleteQuestionAll,
        },
        { height: "auto", scrollable: false, width: "400px" }
      );
    },

    changeLanguage(language) {
      this.selectedLanguage = language;
    },

    addOrUpdateQuestionAll(question) {
      Object.values(this.questionnairesByLanguages).forEach(questionnaire => {
        this.addOrUpdateQuestion(questionnaire, cloneDeep(question));
      });
      this.setValue();
    },

    addOrUpdateQuestion(questionnaire, question) {
      const oldQuestionIndex = questionnaire.questionnaire.findIndex(q => q.ID === question.ID);

      if (oldQuestionIndex !== -1) {
        const oldQ = questionnaire.questionnaire.splice(oldQuestionIndex, 1, question)[0];
        if (this.getSelectedQuestionnaire() !== questionnaire) {
          question.question = oldQ.question;
          if (question.type === 'RADIO') {
            question.answers.forEach(a => {
              const oldA = oldQ.answers.find(oldA => oldA.id === a.id);
              if (oldA !== undefined) {
                a.message = oldA.message;
              } else {
                a.message = '';
              }
            });
          }
        }
      } else {
        questionnaire.questionnaire.push(question);
        if (this.getSelectedQuestionnaire() !== questionnaire) {
          question.question =  '';
          if (question.type === 'RADIO') {
            question.answers.forEach(a => a.message = '');
          }
        }
      }
    },

    deleteQuestionAll(id) {
      Object.values(this.questionnairesByLanguages).forEach(questionnaire => {
        this.deleteQuestion(questionnaire, id);
      });
      this.setValue();
    },

    deleteQuestion(questionnaire, id) {
      questionnaire.questionnaire = questionnaire.questionnaire.filter(q => q.ID !== id);
      questionnaire.questionnaire.forEach(q => {
        if (q.type === 'RADIO') {
          q.answers.forEach(a => {
            if (a.dependency === id) {
              delete a.dependency;
            }
          });
        }
      });
    },

    moveUp(question, shiftKeyPressed) {
      const questionIndex = this.getSelectedQuestionnaire().questionnaire.findIndex(q => q.ID === question.ID);
      if (questionIndex === 0 || questionIndex === -1) {
        return;
      }
      if (shiftKeyPressed) {
        for (let idx = questionIndex; idx > 0; --idx) {
          this.moveToAll(idx - 1, idx);
        }
      } else {
        this.moveToAll(questionIndex - 1, questionIndex);
      }
    },

    moveDown(question, shiftKeyPressed) {
      const questionIndex = this.getSelectedQuestionnaire().questionnaire.findIndex(q => q.ID === question.ID);
      if (questionIndex === this.getSelectedQuestionnaire().questionnaire.length - 1 || questionIndex === -1) {
        return;
      }
      if (shiftKeyPressed) {
        for (let idx = questionIndex; idx < this.getSelectedQuestionnaire().questionnaire.length - 1; ++idx) {
          this.moveToAll(idx + 1, idx);
        }
      } else {
        this.moveToAll(questionIndex + 1, questionIndex);
      }
    },

    moveToAll(newIdx, oldIdx) {
      Object.values(this.questionnairesByLanguages).forEach(questionnaire => {
        this.moveTo(questionnaire, newIdx, oldIdx);
      });
      this.setValue();
    },

    moveTo(questionnaire, newIdx, oldIdx) {
      const q = questionnaire.questionnaire;
      [q[newIdx], q[oldIdx]] = [q[oldIdx], q[newIdx]];
      questionnaire.questionnaire = [ ...q ];
    },

    isSameQuestion(thisQuestion, otherQuestion) {
      return thisQuestion.ID === otherQuestion.ID;
    },

    isDependencyForQuestion(thisQuestion, otherQuestion) {
      return otherQuestion.answers.some(a => a.dependency === thisQuestion.ID);
    },

    otherQuestionHasNonThisDependency(thisQuestion, otherQuestion) {
      return this.getSelectedQuestionnaire().questionnaire.some(q => {
        if (this.isSameQuestion(thisQuestion, q) || this.isSameQuestion(otherQuestion, q)) {
          return false;
        }
        if (this.isDependencyForQuestion(otherQuestion, q)) {
          return true;
        }
        return false;
      });
    },

    wouldFormCyclicalDependency(thisQuestion, otherQuestion) {
      thisQuestion.answers.push({ dependency: otherQuestion.ID });

      function hasCycleDirected(graph) {
          const visited = new Set();
          const stack = new Set();

          function dfs(node) {
              if (stack.has(node)) return true;
              if (visited.has(node)) return false;

              visited.add(node);
              stack.add(node);

              for (let neighbor of graph[node] || []) {
                  if (dfs(neighbor)) return true;
              }

              stack.delete(node);
              return false;
          }

          return Object.keys(graph).some(node => dfs(node));
      }

      const directedGraph = this.getSelectedQuestionnaire().questionnaire.reduce((acc, question) => {
        acc[question.ID] = question.answers.reduce((nAcc, answer) => {
          if (answer.dependency !== null && answer.dependency !== undefined && !nAcc.includes(answer.dependency)) {
            nAcc.push(answer.dependency);
          }
          return nAcc;
        }, []);
        return acc;
      }, {});

      const hasCycle = hasCycleDirected(directedGraph);
      thisQuestion.answers.pop();
      return hasCycle;
    },

    getQuestionsEligibleForDependency(question) {
      return this.getSelectedQuestionnaire().questionnaire.filter(q => {
        if (this.isSameQuestion(question, q)) {
          return false;
        }
        if (this.isDependencyForQuestion(question, q)) {
          return false;
        }
        if (this.otherQuestionHasNonThisDependency(question, q)) {
          return false;
        }
        if (this.wouldFormCyclicalDependency(question, q)) {
          return false;
        }
        return true;
      });
    },

    getDependency(question) {
      return this.getSelectedQuestionnaire().questionnaire.find(q => {
        if (this.isSameQuestion(question, q)) {
          return false;
        }
        return this.isDependencyForQuestion(question, q);
      });
    },

    transformAllForJson(questionnaires) {
      const nQuestionnaires = cloneDeep(questionnaires);
      Object.values(nQuestionnaires).forEach(questionnaire => {
        this.transformForJson(questionnaire);
      })
      return nQuestionnaires;
    },

    transformForJson(questionnaire) {
      questionnaire.questionnaire.forEach(q => {
        if (q.type === 'TEXT') {
          q.minCharacters = !q.minCharacters ? 0 : Number(q.minCharacters);
          delete q.answers;
        } else if (q.type === 'RADIO') {
          q.type = 'TEXT';
          q.answers.forEach(a => {
            a.minCharacters = !a.mandatoryAnswer ? 0 : Number(a.minCharacters);
            if (a.dependency === undefined || a.dependency === '') {
              delete a.dependency;
            }
          })
          delete q.minCharacters;
        } else if (q.type === 'YESNO') {
          q.type = 'QUESTION';
          delete q.minCharacters;
          delete q.answers;
        }
        if (this.getDependency(q) !== undefined) {
          q.notVisible = true;
        }
      });
    },

    transformAllFromJson(questionnaires) {
      const nQuestionnaires = cloneDeep(questionnaires);
      Object.values(nQuestionnaires).forEach(questionnaire => {
        this.transformFromJson(questionnaire);
      })
      return nQuestionnaires;
    },

    transformFromJson(questionnaire) {
      questionnaire.questionnaire.forEach(q => {
        if (q.type === 'TEXT' && q.answers) {
          q.type = 'RADIO';
        } else {
          q.answers = [];
        }
        if (q.type === 'QUESTION') {
          q.type = 'YESNO';
        }
        delete q.notVisible;
      });
    },

    setValue() {
      this.$emit('update', {
        value: JSON.stringify(this.transformAllForJson(this.questionnairesByLanguages)),
        isValid: this.isValid()
      })
    },

    isValid() {
      return true;
    },
  },
}
</script>
<style lang="scss">
.c-table__cell {
  width: 25%!important;
}

.c-table__cell:first-child {
  width: 200px!important;
}

.c-buttons-wrapper {
  display: flex;
  justify-content: end;
}

.c-buttons {
  display: flex;
  flex-direction: column;
}

.c-hint {
  font-size: 12px;
  color: $grey;
}
</style>
